type TransitionPreset = {
  initial?: Record<string, unknown>
  animate?: Record<string, unknown>
  exit?: Record<string, unknown>
}

export type EnterPreset = 'fade' | 'height' | 'width' | 'down' | 'up' | 'left' | 'right' | 'zoom' | 'scaleY' | 'scaleX'
export type ExitPreset = 'fade' | 'height' | 'width' | 'down' | 'up' | 'left' | 'right' | 'zoom' | 'scaleY' | 'scaleX'

const enterPresets: Record<EnterPreset, TransitionPreset> = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
  },
  height: {
    initial: { height: 0 },
    animate: { height: 'auto' },
  },
  width: {
    initial: { width: 0 },
    animate: { width: '100%' },
  },
  scaleX: {
    initial: { scaleX: 0 },
    animate: { scaleX: 1 },
  },
  scaleY: {
    initial: { scaleY: 0 },
    animate: { scaleY: 1 },
  },
  down: {
    initial: { y: 20 },
    animate: { y: 0 },
  },
  up: {
    initial: { y: -20 },
    animate: { y: 0 },
  },
  left: {
    initial: { x: -20 },
    animate: { x: 0 },
  },
  right: {
    initial: { x: 20 },
    animate: { x: 0 },
  },
  zoom: {
    initial: { scale: 0.9 },
    animate: { scale: 1 },
  },
}

const exitPresets: Record<ExitPreset, TransitionPreset> = {
  fade: {
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  height: {
    exit: { height: 0 },
  },
  width: {
    exit: { width: 0 },
  },
  scaleX: {
    exit: { scaleX: 0 },
  },
  scaleY: {
    exit: { scaleY: 1 },
  },
  down: {
    animate: { y: 0 },
    exit: { y: 20 },
  },
  up: {
    animate: { y: 0 },
    exit: { y: -20 },
  },
  left: {
    animate: { x: 0 },
    exit: { x: -20 },
  },
  right: {
    animate: { x: 0 },
    exit: { x: 20 },
  },
  zoom: {
    animate: { scale: 1 },
    exit: { scale: 0.9 },
  },
}

export const easings = {
  linear: 'linear',
  easeOutExpo: [0.22, 0.61, 0.14, 1] as const,
}

type MotionPresetProps = {
  entering: EnterPreset | EnterPreset[] | undefined
  exiting: ExitPreset | ExitPreset[] | undefined
  easing?: PresetEasing
  durationMs?: number
  delayMs?: number
  delayN?: number
  delayStep?: number
  overrides?: Record<string, unknown>
}

export function useMotionPreset({ entering, exiting, easing = 'easeOutExpo', durationMs = 400, delayMs = 0, delayN = 0, delayStep = 50, overrides = {} }: MotionPresetProps) {
  const combinedValues = {
    initial: overrides.initial || {},
    animate: overrides.animate || {},
    exit: overrides.exit || {},
  }

  if (!entering && !exiting) {
    return {}
  }

  const enteringValues = (Array.isArray(entering) ? entering : [entering]).filter(Boolean) as EnterPreset[]
  const exitingValues = (Array.isArray(exiting) ? exiting : [exiting]).filter(Boolean) as ExitPreset[]

  const presetValues = [
    ...enteringValues.map((preset) => enterPresets[preset]),
    ...exitingValues.map((preset) => exitPresets[preset]),
  ]

  presetValues.forEach((animation) => {
    combinedValues.initial = { ...combinedValues.initial, ...animation.initial }
    combinedValues.animate = { ...combinedValues.animate, ...animation.animate }
    combinedValues.exit = { ...combinedValues.exit, ...animation.exit }
  })

  const ease = easings[easing]
  const delay = (delayMs || delayN * delayStep) / 1000
  const duration = durationMs / 1000

  return { ...combinedValues, transition: { duration, delay, ease } }
}

export type PresetEasing = keyof typeof easings